<template>
  <section class="contact-section mt-5">
    <div class="container contact-wrapper mt-3">

      <!-- Page Header -->
      <div class="contact-header">
        <span class="contact-eyebrow">Estamos para ayudarte</span>
        <h1 class="contact-title font-good-brush">Contacto</h1>
        <p class="contact-subtitle">
          Completá el formulario y te respondemos a la brevedad.
        </p>
      </div>

      <div class="row g-5 justify-content-between">

        <!-- Info cards column -->
        <div class="col-lg-4 col-md-5">
          <div class="info-cards">
            <a :href="whatsappUrl" target="_blank" rel="noopener" class="info-card info-card--whatsapp">
              <div class="info-card-icon">
                <i class="fab fa-whatsapp"></i>
              </div>
              <div class="info-card-body">
                <span class="info-card-label">WhatsApp</span>
                <span class="info-card-value">Chateá con nosotros</span>
              </div>
              <i class="fas fa-arrow-right info-card-arrow"></i>
            </a>

            <a :href="`mailto:${contactEmail}`" class="info-card info-card--email">
              <div class="info-card-icon">
                <i class="fas fa-envelope"></i>
              </div>
              <div class="info-card-body">
                <span class="info-card-label">Email</span>
                <span class="info-card-value">{{ contactEmail }}</span>
              </div>
              <i class="fas fa-arrow-right info-card-arrow"></i>
            </a>

            <div class="info-card info-card--hours">
              <div class="info-card-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="info-card-body">
                <span class="info-card-label">Horario de atención</span>
                <span class="info-card-value">Lun – Vie · 10:00 a 20:00</span>
              </div>
            </div>

            <div class="social-row">
              <a v-if="siteConfig.social_instagram" :href="siteConfig.social_instagram" target="_blank" rel="noopener" class="social-btn" title="Instagram">
                <i class="fab fa-instagram"></i>
              </a>
              <a v-if="siteConfig.social_facebook" :href="siteConfig.social_facebook" target="_blank" rel="noopener" class="social-btn" title="Facebook">
                <i class="fab fa-facebook-f"></i>
              </a>
              <a v-if="siteConfig.social_tiktok" :href="siteConfig.social_tiktok" target="_blank" rel="noopener" class="social-btn" title="TikTok">
                <i class="fab fa-tiktok"></i>
              </a>
            </div>
          </div>
        </div>

        <!-- Form column -->
        <div class="col-lg-6 col-md-7 mt-0">

          <!-- Success state -->
          <Transition name="fade-up">
            <div v-if="submitted" class="success-state">
              <div class="success-icon-wrap">
                <i class="fas fa-check"></i>
              </div>
              <h2 class="success-title">¡Mensaje enviado!</h2>
              <p class="success-text">
                Recibimos tu consulta y te responderemos a
                <strong>{{ form.email }}</strong> a la brevedad.
              </p>
              <button class="btn btn-primary px-5" @click="resetForm">
                Enviar otro mensaje
              </button>
            </div>
          </Transition>

          <!-- Form -->
          <Transition name="fade-up">
            <form v-if="!submitted" @submit.prevent="handleSubmit" class="contact-form" novalidate>

              <!-- Name + Email row -->
              <div class="row g-3">
                <div class="col-sm-6">
                  <div class="field-group" :class="{ 'has-error': errors.name, 'is-valid': touched.name && !errors.name }">
                    <label class="field-label" for="cf-name">Nombre *</label>
                    <div class="field-input-wrap">
                      <i class="fas fa-user field-icon"></i>
                      <input
                        id="cf-name"
                        v-model.trim="form.name"
                        type="text"
                        class="field-input"
                        placeholder="Tu nombre"
                        autocomplete="name"
                        @blur="touch('name')"
                      />
                    </div>
                    <Transition name="err">
                      <span v-if="errors.name" class="field-error">{{ errors.name }}</span>
                    </Transition>
                  </div>
                </div>

                <div class="col-sm-6">
                  <div class="field-group" :class="{ 'has-error': errors.email, 'is-valid': touched.email && !errors.email }">
                    <label class="field-label" for="cf-email">Email *</label>
                    <div class="field-input-wrap">
                      <i class="fas fa-envelope field-icon"></i>
                      <input
                        id="cf-email"
                        v-model.trim="form.email"
                        type="email"
                        class="field-input"
                        placeholder="tu@email.com"
                        autocomplete="email"
                        @blur="touch('email')"
                      />
                    </div>
                    <Transition name="err">
                      <span v-if="errors.email" class="field-error">{{ errors.email }}</span>
                    </Transition>
                  </div>
                </div>
              </div>

              <!-- Subject -->
              <div class="field-group mt-3" :class="{ 'has-error': errors.subject, 'is-valid': touched.subject && !errors.subject }">
                <label class="field-label" for="cf-subject">Asunto *</label>
                <div class="field-input-wrap">
                  <i class="fas fa-tag field-icon"></i>
                  <input
                    id="cf-subject"
                    v-model.trim="form.subject"
                    type="text"
                    class="field-input"
                    placeholder="¿En qué te podemos ayudar?"
                    @blur="touch('subject')"
                  />
                </div>
                <Transition name="err">
                  <span v-if="errors.subject" class="field-error">{{ errors.subject }}</span>
                </Transition>
              </div>

              <!-- Message -->
              <div class="field-group mt-3" :class="{ 'has-error': errors.message, 'is-valid': touched.message && !errors.message }">
                <label class="field-label" for="cf-message">
                  Mensaje *
                  <span class="char-counter" :class="{ 'char-counter--warn': form.message.length > 1800 }">
                    {{ form.message.length }}/2000
                  </span>
                </label>
                <textarea
                  id="cf-message"
                  v-model="form.message"
                  class="field-textarea"
                  rows="5"
                  placeholder="Escribí tu consulta con el mayor detalle posible..."
                  maxlength="2000"
                  @blur="touch('message')"
                ></textarea>
                <Transition name="err">
                  <span v-if="errors.message" class="field-error">{{ errors.message }}</span>
                </Transition>
              </div>

              <!-- Rate limit warning -->
              <Transition name="err">
                <div v-if="rateLimited" class="rate-limit-alert">
                  <i class="fas fa-clock me-2"></i>
                  Enviaste demasiados mensajes. Podés intentar de nuevo en <strong>{{ rateLimitCountdown }}</strong>.
                </div>
              </Transition>

              <!-- General error -->
              <Transition name="err">
                <div v-if="generalError" class="general-error-alert">
                  <i class="fas fa-exclamation-circle me-2"></i>
                  {{ generalError }}
                </div>
              </Transition>

              <!-- Submit -->
              <button
                type="submit"
                class="btn btn-primary btn-submit mt-4"
                :disabled="isLoading || rateLimited"
              >
                <span v-if="isLoading" class="d-flex align-items-center justify-content-center gap-2">
                  <span class="spinner-border spinner-border-sm"></span>
                  Enviando...
                </span>
                <span v-else class="d-flex align-items-center justify-content-center gap-2">
                  <i class="fas fa-paper-plane"></i>
                  Enviar mensaje
                </span>
              </button>

              <p class="form-disclaimer">
                <i class="fas fa-lock me-1"></i>
                Tu información es privada y no será compartida con terceros.
              </p>
            </form>
          </Transition>

        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { sendContactMessage } from '@/services/contact';
import { fetchPublicSiteConfig } from '@/services/siteConfig';
import brand from '@/config/brand';

// ── Site config ────────────────────────────────────────────────────
const siteConfig = ref({});

onMounted(async () => {
  try {
    siteConfig.value = (await fetchPublicSiteConfig()) || {};
  } catch {
    // non-critical
  }
});

const contactEmail = computed(() =>
  siteConfig.value.contact_email ||
  siteConfig.value.email ||
  brand.contactEmail
);

const whatsappUrl = computed(() => {
  const raw =
    siteConfig.value.contact_whatsapp ||
    siteConfig.value.contact_phone ||
    siteConfig.value.phone ||
    brand.contactWhatsapp ||
    '';
  const num = String(raw).replace(/\D/g, '') || String(brand.contactWhatsapp || '').replace(/\D/g, '');
  const msg = encodeURIComponent(
    siteConfig.value.whatsapp_message || brand.whatsappMessage
  );
  return `https://wa.me/${num}?text=${msg}`;
});

// ── Form state ─────────────────────────────────────────────────────
const form = reactive({ name: '', email: '', subject: '', message: '' });
const errors = reactive({ name: '', email: '', subject: '', message: '' });
const touched = reactive({ name: false, email: false, subject: false, message: false });

const isLoading  = ref(false);
const submitted  = ref(false);
const rateLimited = ref(false);
const generalError = ref('');

// Rate-limit countdown display (stored in localStorage)
const RATE_LIMIT_KEY = 'contact_rl_until';
const rateLimitCountdown = ref('');

const startCountdown = (untilMs) => {
  const tick = () => {
    const diff = untilMs - Date.now();
    if (diff <= 0) {
      rateLimited.value = false;
      rateLimitCountdown.value = '';
      return;
    }
    const m = Math.floor(diff / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    rateLimitCountdown.value = m > 0 ? `${m}m ${s}s` : `${s}s`;
    setTimeout(tick, 1000);
  };
  tick();
};

onMounted(() => {
  const until = parseInt(localStorage.getItem(RATE_LIMIT_KEY) || '0', 10);
  if (until > Date.now()) {
    rateLimited.value = true;
    startCountdown(until);
  }
});

// ── Validation ─────────────────────────────────────────────────────
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const validate = () => {
  let ok = true;
  errors.name    = form.name.length < 2    ? 'El nombre debe tener al menos 2 caracteres.' : '';
  errors.email   = !EMAIL_RE.test(form.email) ? 'Ingresá un email válido.'                  : '';
  errors.subject = form.subject.length < 3 ? 'El asunto debe tener al menos 3 caracteres.' : '';
  errors.message = form.message.length < 10 ? 'El mensaje debe tener al menos 10 caracteres.'
                 : form.message.length > 2000 ? 'El mensaje no puede superar los 2000 caracteres.'
                 : '';
  if (errors.name || errors.email || errors.subject || errors.message) ok = false;
  return ok;
};

const touch = (field) => {
  touched[field] = true;
  // Re-validate only the touched field
  if (field === 'name')    errors.name    = form.name.length < 2    ? 'El nombre debe tener al menos 2 caracteres.' : '';
  if (field === 'email')   errors.email   = !EMAIL_RE.test(form.email) ? 'Ingresá un email válido.'                  : '';
  if (field === 'subject') errors.subject = form.subject.length < 3 ? 'El asunto debe tener al menos 3 caracteres.' : '';
  if (field === 'message') errors.message = form.message.length < 10 ? 'El mensaje debe tener al menos 10 caracteres.'
                                          : form.message.length > 2000 ? 'El mensaje no puede superar los 2000 caracteres.'
                                          : '';
};

// ── Submit ─────────────────────────────────────────────────────────
const handleSubmit = async () => {
  // Touch all fields to show errors
  Object.keys(touched).forEach(k => { touched[k] = true; });
  if (!validate()) return;

  generalError.value = '';
  isLoading.value = true;

  try {
    await sendContactMessage({ ...form });
    submitted.value = true;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 429) {
      rateLimited.value = true;
      const untilMs = Date.now() + 60 * 60 * 1000;
      localStorage.setItem(RATE_LIMIT_KEY, String(untilMs));
      startCountdown(untilMs);
    } else if (status === 400 && err?.response?.data?.errors) {
      const serverErrors = err.response.data.errors;
      Object.assign(errors, serverErrors);
    } else {
      generalError.value = err?.response?.data?.error
        || 'No se pudo enviar el mensaje. Por favor intentá más tarde.';
    }
  } finally {
    isLoading.value = false;
  }
};

const resetForm = () => {
  Object.assign(form, { name: '', email: '', subject: '', message: '' });
  Object.assign(errors, { name: '', email: '', subject: '', message: '' });
  Object.assign(touched, { name: false, email: false, subject: false, message: false });
  submitted.value = false;
  generalError.value = '';
};
</script>

<style scoped>
/* =============================================
   SECTION LAYOUT
   ============================================= */
.contact-section {
  min-height: 100vh;
  background: var(--bg-secondary);
  padding: 7rem 0 5rem;
}

/* =============================================
   PAGE HEADER
   ============================================= */
.contact-header {
  text-align: start;
  margin-bottom: 3.5rem;
}

.contact-eyebrow {
  display: inline-block;
  background: var(--brand-100);
  color: var(--primary-color);
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  padding: 5px 14px;
  border-radius: 20px;
  margin-bottom: 0.75rem;
}

.contact-title {
  font-size: 2.8rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
  line-height: 1.1;
}

.contact-subtitle {
  font-size: 1.05rem;
  color: var(--text-muted);
  margin: 0;
  max-width: 480px;
}

/* =============================================
   INFO CARDS
   ============================================= */
.info-cards {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #fff;
  border: 1px solid var(--border-light);
  border-radius: 14px;
  padding: 1rem 1.1rem;
  text-decoration: none;
  color: var(--text-primary);
  transition: all var(--transition-base);
  box-shadow: var(--shadow-xs);
}

.info-card:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
  text-decoration: none;
  color: var(--text-primary);
}

.info-card--whatsapp:hover { border-color: #25d366; }
.info-card--email:hover    { border-color: var(--primary-color); }

.info-card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.15rem;
  flex-shrink: 0;
}

.info-card--whatsapp .info-card-icon { background: #e8fdf0; color: #25d366; }
.info-card--email    .info-card-icon { background: var(--brand-100); color: var(--primary-color); }
.info-card--hours    .info-card-icon { background: #eff6ff; color: #3b82f6; }

.info-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-card-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
}

.info-card-value {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
}

.info-card-arrow {
  color: var(--text-muted);
  font-size: 0.8rem;
  transition: transform var(--transition-fast);
}

.info-card:hover .info-card-arrow {
  transform: translateX(3px);
}

/* Social row */
.social-row {
  display: flex;
  gap: 0.6rem;
  padding-top: 0.25rem;
}

.social-btn {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fff;
  border: 1px solid var(--border-color);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-secondary);
  font-size: 0.95rem;
  text-decoration: none;
  transition: all var(--transition-fast);
}

.social-btn:hover {
  background: var(--primary-color);
  color: #fff;
  border-color: var(--primary-color);
  transform: translateY(-2px);
}

/* =============================================
   CONTACT FORM PANEL
   ============================================= */
.contact-form {
  background: #fff;
  border-radius: 18px;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 2rem;
}

/* Field groups */
.field-group {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.field-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.field-input-wrap {
  position: relative;
}

.field-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--text-muted);
  font-size: 0.8rem;
  pointer-events: none;
  transition: color var(--transition-fast);
}

.field-input {
  width: 100%;
  background: var(--gray-50);
  border: 1.5px solid var(--border-color);
  border-radius: 10px;
  padding: 0.65rem 0.85rem 0.65rem 2.2rem;
  font-size: 0.9rem;
  color: var(--text-primary);
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast), background var(--transition-fast);
  outline: none;
}

.field-input:focus {
  border-color: var(--primary-color);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

.field-input:focus + .field-icon,
.field-input-wrap:focus-within .field-icon {
  color: var(--primary-color);
}

/* Textarea */
.field-textarea {
  width: 100%;
  background: var(--gray-50);
  border: 1.5px solid var(--border-color);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.9rem;
  color: var(--text-primary);
  resize: vertical;
  min-height: 130px;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast), background var(--transition-fast);
  outline: none;
  font-family: inherit;
}

.field-textarea:focus {
  border-color: var(--primary-color);
  background: #fff;
  box-shadow: 0 0 0 3px rgba(var(--primary-color-rgb), 0.1);
}

/* Valid / error states */
.field-group.has-error .field-input,
.field-group.has-error .field-textarea {
  border-color: var(--danger-color);
  background: #fff5f5;
}

.field-group.is-valid .field-input,
.field-group.is-valid .field-textarea {
  border-color: var(--success-color);
}

.field-error {
  font-size: 0.75rem;
  color: var(--danger-color);
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Char counter */
.char-counter {
  font-size: 0.72rem;
  color: var(--text-muted);
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.char-counter--warn {
  color: var(--warning-color);
  font-weight: 600;
}

/* Alerts */
.rate-limit-alert,
.general-error-alert {
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-size: 0.85rem;
  font-weight: 500;
  margin-top: 1rem;
  display: flex;
  align-items: center;
}

.rate-limit-alert {
  background: #fffbeb;
  border: 1px solid #fcd34d;
  color: #92400e;
}

.general-error-alert {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

/* Submit button */
.btn-submit {
  width: 100%;
  padding: 0.9rem;
  font-size: 1rem !important;
  font-weight: 700 !important;
  border-radius: 12px !important;
  letter-spacing: 0.02em;
  transition: all var(--transition-base);
}

.btn-submit:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none !important;
}

.form-disclaimer {
  text-align: center;
  font-size: 0.75rem;
  color: var(--text-muted);
  margin: 0.75rem 0 0;
}

/* =============================================
   SUCCESS STATE
   ============================================= */
.success-state {
  background: #fff;
  border-radius: 18px;
  border: 1px solid var(--border-light);
  box-shadow: var(--shadow-sm);
  padding: 3rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.success-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: var(--brand-100);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  color: var(--primary-color);
  animation: pop-in 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes pop-in {
  from { transform: scale(0.5); opacity: 0; }
  to   { transform: scale(1);   opacity: 1; }
}

.success-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0;
}

.success-text {
  color: var(--text-muted);
  font-size: 0.95rem;
  max-width: 380px;
  margin: 0;
}

/* =============================================
   TRANSITIONS
   ============================================= */
.fade-up-enter-active,
.fade-up-leave-active {
  transition: all 0.35s ease;
}

.fade-up-enter-from,
.fade-up-leave-to {
  opacity: 0;
  transform: translateY(16px);
}

.err-enter-active,
.err-leave-active {
  transition: all 0.2s ease;
}

.err-enter-from,
.err-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* =============================================
   RESPONSIVE
   ============================================= */
@media (max-width: 991.98px) {
  .contact-section {
    padding-top: 6rem;
  }

  .contact-title {
    font-size: 2.2rem;
  }

  .info-cards {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .info-card {
    flex: 1 1 calc(50% - 0.5rem);
    min-width: 200px;
  }

  .info-card--hours {
    flex: 1 1 100%;
  }
}

@media (max-width: 575.98px) {
  .contact-section {
    padding-top: 5rem;
    padding-bottom: 3rem;
  }

  .contact-title {
    font-size: 1.8rem;
  }

  .contact-form {
    padding: 1.5rem 1.25rem;
  }

  .info-card {
    flex: 1 1 100%;
  }

  .social-row {
    justify-content: center;
  }
}
</style>
